import { useState, useEffect } from 'react'
import {
  TrendingDown, 
  Users, 
  DollarSign, 
  FileText,
  Activity,
  AlertTriangle,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import { useNavigate } from 'react-router-dom'
import { dashboardApi } from '../utils/api'
import { format } from 'date-fns'

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [balanceAlerts, setBalanceAlerts] = useState([])
  const [topCustomers, setTopCustomers] = useState([])
  const [recentTransactions, setRecentTransactions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [statsRes, alertsRes, customersRes, transactionsRes] = await Promise.all([
        dashboardApi.getStats(),
        dashboardApi.getBalanceAlerts(),
        dashboardApi.getTopCustomers(5),
        dashboardApi.getRecentTransactions(20)
      ])
      
      const formattedTopCustomers = customersRes.data.data.map((customer) => {
        const schoolName = (customer.customer_name || customer.name || customer.display_name || 'Unknown')
          .replace(/（[^）]*）|\([^)]*\)/g, '')
          .replace('科技大學', '科大')
          .trim()
        const unitFromDisplayName = customer.display_name?.match(/[（(]([^）)]*)[）)]/)?.[1] || ''
        const unitName = (customer.company || unitFromDisplayName || '').trim()

        return {
          ...customer,
          chart_display_name: unitName ? `${schoolName}\n${unitName}` : schoolName
        }
      })

      setStats(statsRes.data.data)
      setBalanceAlerts(alertsRes.data.data)
      setTopCustomers(formattedTopCustomers)
      setRecentTransactions(transactionsRes.data.data)
    } catch (error) {
      console.error('載入儀表板數據失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  // 確保數據存在
  const safeStats = stats || {
    total_customers: 0,
    active_customers: 0,
    inactive_customers: 0,
    total_expenses: 0,
    total_budget_balance: 0,
    monthly_expenses: 0
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">儀表板</h1>
          <p className="text-gray-500 mt-1">歡迎回來！查看您的業務概況</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center space-x-2"
        >
          <Activity className="w-4 h-4" />
          <span>重新整理</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="總客戶數"
          value={stats?.total_customers || 0}
          icon={Users}
          color="blue"
          subtitle={`${stats?.active_customers || 0} 啟用中 / ${stats?.inactive_customers || 0} 停用中`}
        />
        <StatCard
          title="代管餘額"
          value={`$${(stats?.total_balance || 0).toLocaleString()}`}
          icon={DollarSign}
          color="green"
          subtitle="所有客戶餘額總和"
        />
        <StatCard
          title="累計支出"
          value={`$${(stats?.total_expenses || 0).toLocaleString()}`}
          icon={TrendingDown}
          color="red"
          subtitle="Azure 費用總計"
        />
        <StatCard
          title="本月入帳支出"
          value={`$${(stats?.monthly_expenses || 0).toLocaleString()}`}
          icon={FileText}
          color="yellow"
          subtitle={`${stats?.monthly_reports || 0} 份入帳報表`}
        />
      </div>

      {/* Row 1: Top Customers (3/5) + Balance Alerts upper (2/5) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Balance Alerts — spans both rows on left */}
        <div className="lg:col-span-2 lg:row-span-2 bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">客戶餘額警示</h2>
          <div className="space-y-2 flex-1 overflow-y-auto pr-1">
            {balanceAlerts.length === 0 ? (
              <div className="text-center py-8 text-gray-400">暫無活躍客戶預算</div>
            ) : (
              balanceAlerts.map((item) => {
                const levelConfig = {
                  danger:  { bg: 'bg-red-50 border-red-200', text: 'text-red-700', bar: 'bg-red-500', icon: AlertCircle, iconColor: 'text-red-500', label: '超支' },
                  warning: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', bar: 'bg-orange-500', icon: AlertTriangle, iconColor: 'text-orange-500', label: '警告' },
                  caution: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', bar: 'bg-yellow-400', icon: AlertTriangle, iconColor: 'text-yellow-500', label: '注意' },
                  normal:  { bg: 'bg-green-50 border-green-200', text: 'text-green-700', bar: 'bg-green-500', icon: CheckCircle, iconColor: 'text-green-500', label: '正常' },
                }[item.level]
                const LevelIcon = levelConfig.icon
                return (
                  <div key={item.customer_id} className={`flex items-center gap-3 p-3 rounded-lg border ${levelConfig.bg}`}>
                    <LevelIcon className={`w-5 h-5 shrink-0 ${levelConfig.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <button
                          type="button"
                          onClick={() => navigate(`/expense-history?customer_id=${encodeURIComponent(item.customer_id)}`)}
                          className="min-w-0 break-words text-left text-sm font-medium text-gray-900 hover:text-primary-600 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                          aria-label={`查看 ${formatDisplayNameWithoutBrackets(item.display_name)} 的費用明細`}
                        >
                          {formatDisplayNameWithoutBrackets(item.display_name)}
                        </button>
                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          <span className="text-xs text-gray-500">最近更新: {item.latest_report_month || '尚無'}</span>
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${levelConfig.text} ${levelConfig.bg}`}>{levelConfig.label}</span>
                        </div>
                      </div>
                      <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${levelConfig.bar}`} style={{ width: `${Math.min(Math.max(item.usage_percentage, 0), 100)}%` }}></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-500">
                          {item.no_prepay
                            ? '餘額 無'
                            : `餘額 $${item.remaining_amount != null ? item.remaining_amount.toLocaleString() : 0}`}
                        </span>
                        <span className="text-xs text-gray-400">已用 {item.usage_percentage}%</span>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Top Customers */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">消費前 5 名客戶</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topCustomers}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="chart_display_name"
                tick={<CustomerNameTick />}
                height={64}
                interval={0}
              />
              <YAxis tickFormatter={formatAmountInTenThousands} />
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '消費金額']} />
              <Bar dataKey="total_spent" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent Transactions — right column, aligned with Top Customers */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">最近交易</h2>
          {recentTransactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">暫無交易記錄</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">客戶</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">類型</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">金額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時間</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        <div>{transaction.customer_name}</div>
                        {transaction.customer_company && (
                          <div className="text-xs text-gray-500">{transaction.customer_company}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          transaction.type === 'recharge' ? 'bg-green-100 text-green-800' :
                          transaction.type === 'consumption' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {transaction.type === 'recharge' ? '充值' : 
                           transaction.type === 'consumption' ? '消費' : '費用報表'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${(transaction.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate" title={transaction.description || '-'}>
                        {transaction.description || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {transaction.created_at ? format(new Date(transaction.created_at), 'MM-dd HH:mm') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`${colorClasses[color]} p-3 rounded-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  )
}

function CustomerNameTick({ x, y, payload }) {
  const [schoolName, unitName] = String(payload.value || '').split('\n')

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#4b5563" fontSize={12}>
        <tspan x={0} dy={12}>{schoolName}</tspan>
        {unitName && <tspan x={0} dy={16}>{unitName}</tspan>}
      </text>
    </g>
  )
}

function formatDisplayNameWithoutBrackets(displayName) {
  return String(displayName || '')
    .replace(/[（(]\s*/g, ' ')
    .replace(/\s*[）)]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function formatAmountInTenThousands(value) {
  return `${(Number(value || 0) / 10000).toLocaleString(undefined, {
    maximumFractionDigits: 1
  })}萬`
}
